import React, { useEffect, useState, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import chatbotImg from "../../assets/images/chatbot.png";
import "./Chatbot.css";

// List of valid countries - you can expand this list as needed
const VALID_COUNTRIES = [
  "afghanistan", "albanien", "algeriet", "andorra", "angola", "antigua og barbuda", "argentina", "armenien", "australien", "østrig",
  "aserbajdsjan", "bahamas", "bahrain", "bangladesh", "barbados", "hviderusland", "belgien", "belize", "benin", "bhutan",
  "bolivia", "bosnien-hercegovina", "botswana", "brasilien", "brunei", "bulgarien", "burkina faso", "burundi", "cambodja", "cameroun",
  "canada", "kap verde", "centralafrikanske republik", "tchad", "chile", "kina", "colombia", "comorerne", "congo", "costa rica",
  "elfenbenskysten", "kroatien", "cuba", "cypern", "tjekkiet", "danmark", "djibouti", "dominica", "dominikanske republik", "østtimor",
  "ecuador", "egypten", "el salvador", "ækvatorialguinea", "eritrea", "estland", "eswatini", "etiopien", "fiji", "finland",
  "frankrig", "gabon", "gambia", "georgien", "tyskland", "ghana", "grækenland", "grenada", "guatemala", "guinea",
  "guinea-bissau", "guyana", "haiti", "honduras", "ungarn", "island", "indien", "indonesien", "iran", "irak",
  "irland", "israel", "italien", "jamaica", "japan", "jordan", "kasakhstan", "kenya", "kiribati", "nordkorea",
  "sydkorea", "kosovo", "kuwait", "kirgisistan", "laos", "letland", "libanon", "lesotho", "liberia", "libyen",
  "liechtenstein", "litauen", "luxembourg", "madagaskar", "malawi", "malaysia", "maldiverne", "mali", "malta", "marshalløerne",
  "mauritanien", "mauritius", "mexico", "mikronesien", "moldova", "monaco", "mongoliet", "montenegro", "marokko", "mozambique",
  "myanmar", "namibia", "nauru", "nepal", "holland", "new zealand", "nicaragua", "niger", "nigeria", "nordmakedonien",
  "norge", "oman", "pakistan", "palau", "palæstina", "panama", "papua ny guinea", "paraguay", "peru", "filippinerne",
  "polen", "portugal", "qatar", "rumænien", "rusland", "rwanda", "saint kitts og nevis", "saint lucia", "saint vincent og grenadinerne", "samoa",
  "san marino", "sao tome og principe", "saudi-arabien", "senegal", "serbien", "seychellerne", "sierra leone", "singapore", "slovakiet", "slovenien",
  "salomonøerne", "somalia", "sydafrika", "sydsudan", "spanien", "sri lanka", "sudan", "surinam", "sverige", "schweiz",
  "syrien", "taiwan", "tadsjikistan", "tanzania", "thailand", "togo", "tonga", "trinidad og tobago", "tunesien", "tyrkiet",
  "turkmenistan", "tuvalu", "uganda", "ukraine", "forenede arabiske emirater", "storbritannien", "usa", "uruguay", "usbekistan", "vanuatu",
  "vatikanstaten", "venezuela", "vietnam", "yemen", "zambia", "zimbabwe"
];

// Create a mapping of common country name variations to the standard names in your list
const COUNTRY_ALIASES = {
  // Bosnia variations
  "bosnia": "bosnien-hercegovina",
  "bosnia and herzegovina": "bosnien-hercegovina",
  "bosnia herzegovina": "bosnien-hercegovina",
  "bosnia & herzegovina": "bosnien-hercegovina",
  
  // Common variations for other countries
  "usa": "usa",
  "united states": "usa",
  "united states of america": "usa",
  "uk": "storbritannien",
  "united kingdom": "storbritannien",
  "great britain": "storbritannien",
  "england": "storbritannien",
  
  // More examples for common variations
  "uae": "forenede arabiske emirater",
  "emirates": "forenede arabiske emirater",
  "holland": "holland",
  "the netherlands": "holland",
  "netherlands": "holland",
  
  // Common variations or misspellings
  "brasil": "brasilien",
  "columbia": "colombia",
  "czeckia": "tjekkiet",
  "czech republic": "tjekkiet",
  "dominican": "dominikanske republik",
  "egypt": "egypten",
  "greece": "grækenland",
  "italy": "italien",
  "morroco": "marokko",
  "morocco": "marokko",
  "new zealand": "new zealand",
  "phillipines": "filippinerne",
  "philippines": "filippinerne",
  "portugal": "portugal",
  "saudiarabia": "saudi-arabien",
  "saudi arabia": "saudi-arabien",
  "slovak": "slovakiet",
  "slovakia": "slovakiet",
  "south africa": "sydafrika",
  "spain": "spanien",
  "sri lanka": "sri lanka",
  "sweden": "sverige",
  "switzerland": "schweiz",
  "thai": "thailand",
  "turkey": "tyrkiet",
  "turkiye": "tyrkiet",
  "vietnam": "vietnam"
};

const Chatbot = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);

  const [currentStep, setCurrentStep] = useState(-1);
  const [countries, setCountries] = useState([]);
  const [currentCountry, setCurrentCountry] = useState(0);
  const [isTypewriterActive, setIsTypewriterActive] = useState(false);

  const addMessageWithTypewriter = (newMessage) => {
    return new Promise((resolve) => {
      setIsTypewriterActive(true);
      setMessages((prev) => [...prev, { ...newMessage, content: "" }]);

      let index = 0;
      const interval = setInterval(() => {
        if (index < newMessage.content.length) {
          setMessages((prev) => {
            const updated = [...prev];
            updated[updated.length - 1] = {
              ...updated[updated.length - 1],
              content: newMessage.content.substring(0, index + 1),
            };
            return updated;
          });
          index++;
        } else {
          clearInterval(interval);
          setIsTypewriterActive(false);
          resolve();
        }
      }, 15);
    });
  };

  useEffect(() => {
    addMessageWithTypewriter({
      role: "system",
      content:
        "Hej! Jeg hjælper dig med hvilke vacciner der anbefales til din rejse.\n\nHvilket land eller hvilke lande rejser du til? (Angiv venligst hvert land adskilt af kommaer)",
    });
  }, []);

  const questions = [
    (country) => `Hvor længe skal du opholde dig i ${country}?`,
    (country) =>
      `Skal du opholde dig i bjerge, skove eller landområder i ${country}? (svar venligst ja eller nej)`,
    (country) =>
      `Skal du være i tæt kontakt med den lokale befolkning i ${country}? (svar venligst ja eller nej)`,
    (country) =>
      `Skal du bo hos en lokal familie i ${country}? (svar venligst ja eller nej)`,
    (country) =>
      `Skal du have tæt kontakt med lokale dyr i ${country}? (svar venligst ja eller nej)`,
    (country) =>
      `Planlægger du at få tatoveringer, gennemgå kirurgiske indgreb 
eller arbejde i sundhedssektoren i ${country}? (svar venligst ja eller nej)`,
  ];

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    resizeTextarea();
  }, [inputValue]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  };

  // Updated function to validate if a country exists
  const isValidCountry = (country) => {
    const normalizedCountry = country.toLowerCase().trim();
    
    // Direct match in our VALID_COUNTRIES list
    if (VALID_COUNTRIES.includes(normalizedCountry)) {
      return {
        valid: true,
        standardName: normalizedCountry
      };
    }
    
    // Check for alias match
    if (COUNTRY_ALIASES[normalizedCountry]) {
      return {
        valid: true,
        standardName: COUNTRY_ALIASES[normalizedCountry]
      };
    }
    
    // Fuzzy matching - check if the input is a substring of any valid country
    for (const validCountry of VALID_COUNTRIES) {
      if (validCountry.includes(normalizedCountry) || normalizedCountry.includes(validCountry)) {
        return {
          valid: true,
          standardName: validCountry
        };
      }
    }
    
    // No match found
    return {
      valid: false,
      standardName: null
    };
  };

  // Updated function to validate all countries from user input
  const validateCountries = (countryList) => {
    const validatedCountries = [];
    const invalidCountries = [];
    
    countryList.forEach(country => {
      const result = isValidCountry(country);
      if (result.valid) {
        validatedCountries.push(result.standardName);
      } else {
        invalidCountries.push(country);
      }
    });
    
    return { validatedCountries, invalidCountries };
  };

  const handleSend = async () => {
    if (!inputValue.trim() || isTypewriterActive) {
      return;
    }

    // Add user message to chat
    const userMessage = { role: "user", content: inputValue.trim() };
    setMessages((prev) => [...prev, userMessage]);

    const userInput = inputValue.trim();
    setInputValue("");

    // Handle initial country input
    if (currentStep === -1) {
      const countryList = userInput.split(",").map((country) => country.trim());
      
      // Validate countries
      const { validatedCountries, invalidCountries } = validateCountries(countryList);
      
      if (invalidCountries.length > 0) {
        // Some countries are invalid - inform the user
        await addMessageWithTypewriter({
          role: "system",
          content: `Jeg kunne ikke genkende følgende land(e): ${invalidCountries.join(", ")}. Prøv venligst igen med gyldige landenavne.`,
        });
        return;
      }
      
      if (validatedCountries.length === 0) {
        // No valid countries were entered
        await addMessageWithTypewriter({
          role: "system",
          content: "Jeg kunne ikke genkende nogle lande fra din indtastning. Indtast venligst gyldige landenavne adskilt af kommaer.",
        });
        return;
      }
      
      // All countries are valid - proceed with the flow
      setCountries(validatedCountries);
      setCurrentStep(0);
      await addMessageWithTypewriter({
        role: "system",
        content: questions[0](validatedCountries[0]),
      });
      return;
    }

    // Handle email input step
    if (currentStep === "waitingForEmail") {
      if (!validateEmail(userInput)) {
        await addMessageWithTypewriter({
          role: "system",
          content:
            "Ugyldigt e-mail-format. Indtast venligst en gyldig e-mailadresse.",
        });
        return;
      }

      // Send vaccine recommendations via email
      try {
        await axios.post(
          "https://chatbot-backend-production-1eaa.up.railway.app/mail",
          {
            recipient: userInput,
            text: emailRecommendationText, // Stores ChatGPT's response from previous step
          }
        );

        await addMessageWithTypewriter({
          role: "system",
          content:
            "Dine vaccineanbefalinger er blevet sendt til din e-mail! ✅. Har du ikke fået vores mail, da tjek venligst din spam-indbakke.",
        });

        await addMessageWithTypewriter({
          role: "system",
          content: `Bemærk venligst, at de anbefalede vacciner er vores generelle anbefalinger. Bestil en tid på vores vaccinationsklinik for at få dine personlige vaccinationsanbefalinger og rejsevacciner, så du sikrer dig, at du er fuldt dækket inden din rejse. <a style="margin-top:4px; display:block;width:fit-content; background-color: #BE123C; color: white; padding: 10px 16px; border-radius: 4px; font-size: medium; text-decoration: none;" href="https://system.easypractice.net/book/damir-1">Bestil tid</a>`,
        });

        setCurrentStep(-1); // Reset conversation
        setEmailRecommendationText(null); // Clear stored ChatGPT response
      } catch (error) {
        toast.error("Failed to send email. Please try again.");
      }
      return;
    }

    // Handle responses to questions
    if (currentStep < questions.length) {
      if (currentStep === questions.length - 1) {
        if (currentCountry < countries.length - 1) {
          setCurrentCountry((prev) => prev + 1);
          setCurrentStep(0);
          await addMessageWithTypewriter({
            role: "system",
            content: questions[0](countries[currentCountry + 1]),
          });
        } else {
          // All questions completed - Get ChatGPT recommendation
          setIsTyping(true);
          try {
            const apiResponse = await chatgptAPICall(messages);
            setIsTyping(false);

            // Store ChatGPT response instead of displaying it
            setEmailRecommendationText(apiResponse);

            // Ask for email
            await addMessageWithTypewriter({
              role: "system",
              content:
                "Indtast venligst din e-mail, så sender vi dig vaccineanbefalingerne til din rejse.",
            });

            setCurrentStep("waitingForEmail");
          } catch (error) {
            toast.error("Failed to get recommendations. Please try again.");
          }
        }
      } else {
        setCurrentStep((prev) => prev + 1);
        await addMessageWithTypewriter({
          role: "system",
          content: questions[currentStep + 1](countries[currentCountry]),
        });
      }
    }
  };

  // Email validation function
  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Stores vaccine recommendations before sending via email
  const [emailRecommendationText, setEmailRecommendationText] = useState(null);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSend();
    }
  };

  const resizeTextarea = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      const scrollHeight = textarea.scrollHeight;
      const lineHeight = parseInt(
        window.getComputedStyle(textarea).lineHeight,
        10
      );
      const rows = Math.floor(scrollHeight / lineHeight);
      const maxRows = 5;

      if (rows > maxRows) {
        textarea.style.height = `${lineHeight * maxRows}px`;
        textarea.style.overflowY = "auto";
      } else {
        textarea.style.height = `${scrollHeight}px`;
        textarea.style.overflowY = "hidden";
      }
    }
  };

  const chatgptAPICall = async (prompt) => {
    try {
      // Step 1: Get the threadId
      const threadRes = await fetch(
        "https://chatbot-backend-production-1eaa.up.railway.app/thread",
        {
          method: "GET",
        }
      );

      if (!threadRes.ok) {
        throw new Error("Failed to fetch thread ID");
      }

      const threadData = await threadRes.json();
      const threadId = threadData.threadId;

      const response = await axios.post(
        "https://chatbot-backend-production-1eaa.up.railway.app/message",
        {
          threadId,
          userMessage: JSON.stringify(messages),
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      return response.data.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  useEffect(() => {
    if (!isTypewriterActive && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isTypewriterActive, messages]);

  return (
    <div className="w-full h-full flex flex-col justify-between px-2 py-4">
      <ToastContainer />

      <div className="w-full max-w-4xl mx-auto flex flex-col flex-grow items-center shadow rounded-lg">
        <div className="w-full bg-rose-700 max-h-16 flex items-center pl-2 py-1.5">
          <img
            src={chatbotImg}
            className="rounded-full w-12 h-12 border-2 border-white mr-2"
            alt="Avatar"
          />
          <h2 className="text-white text-lg">Anna, Vaccineekspert</h2>
        </div>

        <div className="p-2 m-0 w-full rounded-lg mt-4">
          <div
            id="msg"
            ref={messagesContainerRef}
            className="enhanced-textarea overflow-y-auto p-2 pl-0"
          >
            {messages.map((message, index) => (
              <div
                key={index}
                className={`mb-4 ${
                  message.role === "system" ? "text-left" : "text-right"
                }`}
              >
                <span
                  className={`inline-block p-2 max-w-[80%] rounded-lg ${
                    message.role === "system"
                      ? "bg-white rounded-br-none"
                      : "text-start bg-gray-200 rounded-bl-none"
                  }`}
                >
                  {message.role === "system" ? (
                    <div
                      dangerouslySetInnerHTML={{ __html: message.content }}
                    />
                  ) : (
                    <pre>{message.content}</pre>
                  )}
                </span>
              </div>
            ))}

            {isTyping && (
              <div className="text-left mb-2">
                <span className="inline-block p-2 max-w-[80%] bg-gray-200 rounded-lg animate-pulse">
                  Generering af resultater...
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto fixed bottom-4 left-0 right-0 w-full bg-white p-2 border-t border-gray-200">
        <div className="pb-2  flex gap-2 items-end justify-center">
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="Skriv her..."
            className="flex-grow p-2 bg-white border border-gray-400 rounded-md outline-none focus:outline-none resize-none overflow-y-hidden"
            rows="1"
            disabled={isTypewriterActive}
          />
          <button
            onClick={handleSend}
            className="bg-rose-700 text-white rounded-md px-4 py-2"
            disabled={isTypewriterActive}
          >
            <i className="fa-solid fa-location-arrow"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Chatbot;